import type { RootOptions } from "react-dom/client";

import { reportClientError } from "./errors";
import type { RumApp } from "./types";

type ReactErrorKind = "caught" | "uncaught" | "recoverable";
type ReactErrorInfo = { componentStack?: string };

function reportReactError(
  app: RumApp,
  kind: ReactErrorKind,
  error: unknown,
  info: ReactErrorInfo,
): void {
  const scope = `react.${app}`;
  const message = `${kind} render error`;
  const data = info.componentStack ? { componentStack: info.componentStack } : undefined;

  reportClientError(scope, message, data, error);

  // Supplying a root callback replaces React's default production console reporter. Keep a local
  // diagnostic even when the page is sampled out of RUM; recoverable errors are warnings because
  // React repaired the tree, while caught/uncaught failures remain errors.
  if (kind === "recoverable") console.warn(`[${scope}] ${message}`, error, info);
  else console.error(`[${scope}] ${message}`, error, info);
}

/** React 19 production root callbacks wired to Mira's bounded client-error pipeline. */
export function createRootErrorHandlers(app: RumApp): RootOptions {
  return {
    onCaughtError: (error, info) => reportReactError(app, "caught", error, info),
    onUncaughtError: (error, info) => reportReactError(app, "uncaught", error, info),
    onRecoverableError: (error, info) => reportReactError(app, "recoverable", error, info),
  };
}
